<script setup lang="ts" generic="T">
	import type { RecordSchema } from '@/types'
	import { onClickOutside, useElementBounding, useWindowSize } from '@vueuse/core'
	import type { ClassNameValue } from 'tailwind-merge'

	export type UIDropDownOption = {
		disabled?: boolean
		label: string
		value?: string | number | null | boolean | undefined
		icon?: string
		iconDir?: string
		iconSize?: number
		className?: ClassNameValue
		callback?: () => void
		href?: string
	}

	interface Props {
		dropdownClasses?: ClassNameValue
		showArrow?: boolean
		options?: (UIDropDownOption & T)[]
		optionClass?: ClassNameValue
		closeOnClickOutside?: boolean
		ignoreClasses?: string[]
	}

	const { showArrow = false, closeOnClickOutside = true, ignoreClasses = [] } = defineProps<Props>()
	const emit = defineEmits<{ (e: 'change', param?: unknown): void }>()
	const modelValue = defineModel<string | number | RecordSchema<T> | null>({ default: '', required: false })

	const isOpen = shallowRef(false)
	const triggerRef = ref<HTMLElement | null>(null)
	const menuRef = ref<HTMLElement | null>(null)

	const { top, left, width, height } = useElementBounding(triggerRef)
	const { height: windowHeight, width: windowWidth } = useWindowSize()

	const showAbove = ref(false)
	const showRight = ref(false)

	const toggleDropdown = (k: unknown = undefined) => {
		if (isOpen.value) {
			isOpen.value = false
		} else {
			// Calculate vertical positioning
			const spaceBelow = windowHeight.value - (top.value + height.value)
			const spaceAbove = top.value
			showAbove.value = spaceBelow < 250 && spaceAbove > spaceBelow

			// Calculate horizontal positioning
			// Check if menu would overflow right edge if left-aligned
			const estimatedWidth = Math.max(200, width.value)
			const spaceRight = windowWidth.value - left.value
			showRight.value = spaceRight < estimatedWidth && left.value > estimatedWidth

			isOpen.value = true
		}

		if (k !== undefined && k !== null && !(k instanceof Event)) {
			emit('change', k)
			modelValue.value = k as RecordSchema<T>
		}
	}

	const close = () => {
		isOpen.value = false
	}

	const menuStyle = computed(() => {
		const isAbove = showAbove.value
		const isRight = showRight.value

		const style: Record<string, string | number> = {
			position: 'fixed' as const,
			top: isAbove ? `${top.value - 8}px !important` : `${top.value + height.value + 8}px !important`,
			transform: isAbove ? 'translateY(-100%)' : 'none',
			width: 'max-content !important',
			minWidth: `${Math.max(160, width.value)}px !important`,
			maxWidth: `calc(${windowWidth.value}px - ${left.value + 16}px) !important`,
			maxHeight: isAbove
				? `${top.value - 32}px !important`
				: `${windowHeight.value - (top.value + height.value + 32)}px !important`,
			zIndex: 600,
			display: 'flex',
			flexDirection: 'column'
		}

		if (isRight) {
			style.right = `${windowWidth.value - (left.value + width.value)}px !important`
			style.left = 'auto !important'
			style.maxWidth = `calc(${left.value + width.value}px - 16px) !important`
		} else {
			style.left = `${left.value}px !important`
			style.right = 'auto !important'
		}

		return style
	})

	onClickOutside(menuRef, event => {
		if (!closeOnClickOutside) return
		if (triggerRef.value?.contains(event.target as Node)) return

		// Check if click is on an Ant Design teleported element or custom ignored classes
		const target = event.target as HTMLElement
		const isIgnored = [
			'ant-menu-submenu-popup',
			'ant-picker-dropdown',
			'ant-select-dropdown',
			'ant-dropdown-menu-submenu',
			'ant-calendar-picker-container',
			'ant-modal-mask',
			'ant-modal-wrap',
			'popup-content',
			...ignoreClasses
		].some(cls => target.closest(`.${cls}`))

		if (isIgnored) return

		close()
	})

	const handleKeydown = (event: KeyboardEvent) => {
		if (!isOpen.value) {
			if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
				event.preventDefault()
				isOpen.value = true
			}
			return
		}

		if (event.key === 'Escape') {
			event.preventDefault()
			close()
		}
	}
</script>

<template>
	<div ref="triggerRef" class="inline-block w-fit" @keydown="handleKeydown">
		<div
			class="w-full cursor-pointer focus:outline-none"
			:aria-expanded="isOpen"
			:aria-haspopup="true"
			role="button"
			tabindex="0"
			@click="toggleDropdown()"
		>
			<slot name="trigger" :toggle="toggleDropdown" />
		</div>

		<teleport to="body">
			<Transition
				enter-active-class="transition duration-300 ease-out"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-active-class="transition duration-200 ease-in"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<div v-if="isOpen" class="fixed inset-0 z-[500] bg-black/10 backdrop-blur-[1px]" @click="close" />
			</Transition>
		</teleport>

		<teleport to="body">
			<Transition
				appear
				enter-active-class="transition duration-300 ease-out-quart"
				enter-from-class="opacity-0 scale-95"
				enter-to-class="opacity-100 scale-100"
				leave-active-class="transition duration-200 ease-in-quart"
				leave-from-class="opacity-100 scale-100"
				leave-to-class="opacity-0 scale-95"
			>
				<div v-if="isOpen" ref="menuRef" :style="menuStyle" :class="cn('z-[600]', dropdownClasses)" role="menu">
					<div
						class="dropdown-content custom-scrollbar relative flex-1 overflow-y-auto rounded-xl border border-gray-100 bg-white py-1.5 shadow-2xl shadow-black/10"
						:class="[
							{ 'dropdown-content--arrow': showArrow },
							showAbove ? 'dropdown-content--arrow-bottom' : 'dropdown-content--arrow-top',
							showRight ? 'dropdown-content--arrow-right' : 'dropdown-content--arrow-left'
						]"
					>
						<slot :toggle="toggleDropdown">
							<template v-for="(item, index) in options" :key="`edit-${index}`">
								<nuxt-link
									v-if="item.href && !item?.disabled"
									ref="itemRefs"
									:to="item.href"
									role="menuitem"
									tabindex="-1"
									:class="
										cn(
											'dropdown-item inline-flex cursor-pointer items-center gap-3 transition-all duration-200 hover:bg-gray-50',
											item.value === modelValue || item.label === modelValue
												? 'bg-primary-2/5 font-semibold text-primary-2'
												: 'text-gray-700',
											optionClass,
											item.className
										)
									"
									@click="
										() => {
											toggleDropdown(item?.value ?? item.label)
											item?.callback?.()
										}
									"
								>
									<tools-icon
										v-if="item.icon"
										:icon="item.icon"
										:dir="item.iconDir"
										:width="item.iconSize ?? 18"
										:height="item.iconSize ?? 18"
										class="opacity-70"
									/>
									<span class="flex-1 whitespace-nowrap text-left">{{ item.label }}</span>
								</nuxt-link>

								<button
									v-else-if="!item?.disabled"
									ref="itemRefs"
									type="button"
									role="menuitem"
									tabindex="-1"
									:class="
										cn(
											'dropdown-item inline-flex cursor-pointer items-center gap-3 transition-all duration-200 hover:bg-gray-50',
											item.value === modelValue || item.label === modelValue
												? 'bg-primary-2/5 font-semibold text-primary-2'
												: 'text-gray-700',
											optionClass,
											item.className
										)
									"
									@click="
										() => {
											toggleDropdown(item?.value ?? item.label)
											item?.callback?.()
										}
									"
								>
									<tools-icon
										v-if="item.icon"
										:icon="item.icon"
										:dir="item.iconDir"
										:width="item.iconSize ?? 18"
										:height="item.iconSize ?? 18"
										class="opacity-70"
									/>
									<span class="flex-1 whitespace-nowrap text-left">{{ item.label }}</span>
								</button>
							</template>
						</slot>
					</div>
				</div>
			</Transition>
		</teleport>
	</div>
</template>

<style scoped>
	.ease-out-quart {
		transition-timing-function: cubic-bezier(0.25, 1, 0.5, 1);
	}
	.ease-in-quart {
		transition-timing-function: cubic-bezier(0.75, 0, 1, 1);
	}

	.dropdown-content--arrow::after {
		content: '';
		@apply absolute h-5 w-5 bg-white;
	}

	.dropdown-content--arrow-left::after {
		@apply left-[20px];
	}

	.dropdown-content--arrow-right::after {
		@apply right-[20px];
	}

	.dropdown-content--arrow-top::after {
		@apply top-[-20px];
		clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
	}

	.dropdown-content--arrow-bottom::after {
		@apply bottom-[-20px];
		clip-path: polygon(50% 100%, 0% 0%, 100% 0%);
	}

	.dropdown-item {
		@apply block w-full border-b border-gray-50 px-5 py-2.5 text-left text-[14px] font-medium leading-normal no-underline decoration-transparent outline-none last:border-none focus:bg-gray-50;
	}

	.custom-scrollbar::-webkit-scrollbar {
		width: 4px;
	}

	.custom-scrollbar::-webkit-scrollbar-track {
		@apply bg-transparent;
	}

	.custom-scrollbar::-webkit-scrollbar-thumb {
		@apply rounded-full bg-gray-200;
	}

	.custom-scrollbar::-webkit-scrollbar-thumb:hover {
		@apply bg-gray-300;
	}
</style>
